import FormApprovalUnfinished from "../../components/Form/FormApproval/FormApprovalUnfinished";
import FormApprovalComplete from "../../components/Form/FormApproval/FormApprovalComplete";
import FormChoosePermits from "../../components/Form/FormChoosePermits/FormChoosePermits";
import FormEducation from "../../components/Form/FormEducation/FormEducation";
import FormTitle from "../../components/Form/FormTitle/FormTitle";

import { SyntheticEvent, useEffect, useState } from "react";

import classes from "./FormPage.module.css";

// Import contexts to store originalData and currentData
import { useOriginalDataContext } from "../../contexts/OriginalDataContext";
import { useCurrentDataContext } from "../../contexts/CurrentDataContext";
import { Employee, FormData } from "../../contexts/interfaces";
import { useNavigate, useParams } from "react-router-dom";
import { useUserContext } from "../../contexts/UserContext";
import { getEmployee, updateForm } from "../../scripts/dataHandlers";
import ReactLoading from "react-loading";
import { useTranslation } from "react-i18next";
import FormEmployeeSearch from "../../components/Form/FormEmployeeSearch/FormEmployeeSearch";
import TitleLine from "../../components/Assets/TitleLine/TitleLine";
import FormChosenEmployee from "../../components/Form/FormChosenEmployee/FormChosenEmployee";
import FormNotActive from "../../components/Form/FormNotActive/FormNotActive";
import axios from "axios";
import { useRoutesContext } from "../../contexts/RoutesContext";
import { toast } from "react-toastify";
import { useRequiredAccomplishmentsContext } from "../../contexts/RequiredAccomplishmentsContext";
import { useErrorContext } from "../../contexts/ErrorContext";
import { requiredVaccination } from "../../utils/requiredVaccination";

const FormPage = () => {
  const { t } = useTranslation();

  const { empUnit, empId, formId } = useParams();

  const [employee, setEmployee] = useState<Employee | null>();
  const [loading, setLoading] = useState<boolean>(false);

  // Data contexts for form data
  const { originalData, setOriginalData } = useOriginalDataContext();
  const { currentData, setCurrentData } = useCurrentDataContext();
  const { requiredAccomplishments, setRequiredAccomplishments } =
    useRequiredAccomplishmentsContext();

  const { routes } = useRoutesContext();

  const navigate = useNavigate();

  const { user } = useUserContext();

  const { handleError } = useErrorContext();

  useEffect(() => {
    // Create cancellation token for axios
    const CancelToken = axios.CancelToken;
    const source = CancelToken.source();

    const fetchData = async () => {
      // Get data for employee, using employeeId from url and userdata from context
      // unitId is set as undefined, as we are only asking for one employee
      const response =
        user &&
        (await getEmployee(
          user.id,
          Number(empUnit),
          Number(empId),
          Number(formId),
          source
        ));
      if (response?.status === 200) {
        const empData = { ...response?.data?.result[0] };

        // Set employee to context
        setEmployee({ ...empData });

        console.log("Response: ", response);
        // Take formData from employee and set it to currentData and originalData contexts
        setOriginalData({ ...empData.formData });
        setCurrentData({ ...empData.formData });
      } else {
        handleError(response, "GetEmployeeFailed");
      }
    };

    // If empId in url and user in context exists, fetch data for chosen employee, else just stay in view and search component is rendered
    if (user && empId) {
      fetchData();
    }

    return () => source.cancel();
  }, [
    user,
    empId,
    formId,
    navigate,
    setCurrentData,
    setOriginalData,
    empUnit,
    handleError,
  ]);

  useEffect(() => {
    // Modify required accomplishments to include vaccination object, if checkbox for vaccination permit is checked
    if (
      currentData?.permits?.find((p) => p.permitItemId === 8)?.isChecked ===
        false &&
      requiredAccomplishments?.findIndex(
        (a) => a.type === requiredVaccination.type
      ) !== -1
    ) {
      requiredAccomplishments &&
        setRequiredAccomplishments([
          ...requiredAccomplishments.filter(
            (acc) => acc.type !== requiredVaccination.type
          ),
        ]);
    } else if (
      currentData?.permits?.find((p) => p.permitItemId === 8)?.isChecked ===
        true &&
      requiredAccomplishments?.findIndex(
        (a) => a.type === requiredVaccination.type
      ) === -1
    ) {
      requiredAccomplishments &&
        setRequiredAccomplishments([
          ...requiredAccomplishments,
          requiredVaccination,
        ]);
    }
  }, [
    currentData?.accomplishments,
    currentData?.permits,
    requiredAccomplishments,
    setRequiredAccomplishments,
  ]);

  const clearContexts = () => {
    setOriginalData(undefined);
    setCurrentData(undefined);
    setEmployee(undefined);
  };

  // Function which handles form data, when submit button is clicked
  const formSubmitHandler = async (
    e: SyntheticEvent<HTMLFormElement> | undefined,
    step: string,
    unfinished?: string | undefined
  ) => {
    e?.preventDefault();

    // Create info message about form saving
    toast(`${t("message.savingForm")}`, {
      toastId: "updateForm",
      position: "top-center",
      autoClose: false,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
      type: "default",
    });

    if (currentData && requiredAccomplishments) {
      // Copy currentData into new obj and update it's formStatus into step
      // (If called inside "approval" -step, sets formStatus to "waiting")
      let newCurrentData: FormData = { ...currentData, formStatus: step };

      if (step === "waiting" && currentData?.accomplishments && user?.id) {
        //**  FORM IS GOING TO BE SENT INTO DOCTOR APPROVAL, CHECK/ADD ACCOMPLISHMENTS AND ATTACH FOREMANID**//

        // Attach approving foreman's id to formData
        newCurrentData = {
          ...newCurrentData,
          foremanId: user?.id,
          unitId: employee?.expenseUnit,
          unitName: employee?.expenseUnitName,
        };

        // Go through required accomplishments array and check if currentData OR employeeData has matches
        let finalAccomplishments = requiredAccomplishments.map((reqA) => {
          if (
            currentData.accomplishments.findIndex(
              (a) => a.type === reqA.type
            ) !== -1
          ) {
            // If currentData has a match in type, use that as accomplishment (prioritizing manually attached over real-time data)
            return {
              ...currentData.accomplishments.find(
                (acc) => acc.type === reqA.type && acc.date !== null
              ),
            };
          } else if (
            employee?.accomplishments.findIndex((a) => a.type === reqA.type) !==
            -1
          ) {
            // If employee has a match in type, use that as accomplishment
            return {
              ...employee?.accomplishments.find(
                (acc) => acc.type === reqA.type && acc.date !== null
              ),
            };
          } else {
            // Show error
            handleError(
              new Error("Accomplishments not found"),
              "AccomplishmentsNotFound"
            );
            return null;
          }
        });
        // Fill new currentData with final accomplishments
        newCurrentData = {
          ...newCurrentData,
          //@ts-ignore
          accomplishments: [...finalAccomplishments],
        };
      }

      // Compare stringified originalData and newCurrentData. If there's any differences, update data to DB
      if (
        JSON.stringify({ ...originalData }) !==
        JSON.stringify({ ...newCurrentData })
      ) {
        const res =
          user &&
          employee &&
          currentData &&
          (await updateForm(
            user.id,
            employee.employeeId,
            Number(formId) || employee.formId,
            newCurrentData
          ));
        console.log("Res in save: ", res);
        if (res?.status === 200) {
          console.log("Res in save: ", res);

          if (unfinished === "unfinished") {
            clearContexts();
            navigate(user ? `/${routes.main}` : "/");
          } else if (unfinished === "unfinishedStay") {
            clearContexts();
            navigate(user ? `/${routes.form}` : "/");
          } else if (employee && !formId && res?.data[0]) {
            setEmployee({
              ...employee,
              formId: res.data[0],
              formStatus: "permits",
            });
            newCurrentData = {
              ...newCurrentData,
              formId: res.data[0],
              formStatus: "permits",
              employeeId: employee.employeeId,
            };
            navigate(`/${routes.form}/${empUnit}/${empId}/${res.data[0]}`);
          }
          // If response was successful, set currentData in context to updated version (updating it's step)
          setCurrentData(JSON.parse(JSON.stringify({ ...newCurrentData }))); // parseStringify = deep copy

          // Set original data again, to notice differences in next steps
          setOriginalData(JSON.parse(JSON.stringify({ ...newCurrentData }))); // parseStringify = deep copy
        } else if (res?.data === "AccomplishmentTooOld") {
          handleError(res, "AccomplishmentTooOld");
        } else if (res?.data === "AccomplishmentMissing") {
          handleError(res, "AccomplishmentMissing");
        } else {
          handleError(res, "UpdateFormFailed");
        }

        setLoading(false);
      } else if (unfinished === "unfinished") {
        clearContexts();
        navigate(user ? `/${routes.main}` : "/");
      } else if (unfinished === "unfinishedStay") {
        clearContexts();
        navigate(user ? `/${routes.form}` : "/");
      }
    }
    // Remove info message about form saving
    toast.dismiss("updateForm");
  };
  if (!empId) {
    // If there's no employeeId in URL yet, show search and inactive form elements
    return (
      <div className="mainContainer">
        <div className={classes.container}>
          <FormTitle />
          <TitleLine />
          <FormEmployeeSearch />
          <FormNotActive
            title={`1 ${t("formChoosePermits.mainTitle")}`}
            text={`${t("formChoosePermits.noSelections")}`}
          />
          <FormNotActive
            title={`2 ${t("formChoosePermits.mainTitle")}`}
            text={`${t("formChoosePermits.noSelections")}`}
          />
          <FormNotActive
            title={`3 ${t("formChoosePermits.mainTitle")}`}
            text={`${t("formChoosePermits.noSelections")}`}
          />
        </div>
      </div>
    );
  } else {
    // If there's employeeId in URL, load data with it and show form elements
    // or loading spinner, if still loading
    return (
      <div className="mainContainer">
        <div className={classes.container}>
          {employee && requiredAccomplishments ? (
            <>
              <FormTitle />
              <TitleLine />
              <FormChosenEmployee
                employee={employee}
                formSubmitHandler={formSubmitHandler}
              />

              <FormEducation
                formSubmitHandler={formSubmitHandler}
                setLoading={setLoading}
              />

              <FormChoosePermits
                formSubmitHandler={formSubmitHandler}
                setLoading={setLoading}
                chosenEmployee={employee}
              />

              {currentData?.formStatus !== "waiting" ? (
                <FormApprovalUnfinished
                  formSubmitHandler={formSubmitHandler}
                  setLoading={setLoading}
                  employeeAccomplishments={employee.accomplishments}
                  employeeExpenseUnitId={employee.expenseUnit}
                />
              ) : (
                <FormApprovalComplete loading={loading} />
              )}
            </>
          ) : (
            <div className={classes.loadingContainer}>
              <ReactLoading
                type={"spokes"}
                color={"#ED1B24"}
                height={"50px"}
                width={"50px"}
              />

              <div className={classes.profileLoadingText}>
                {t("form.loading")}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }
};

export default FormPage;
